import React, { useState, forwardRef, useImperativeHandle, useEffect, useCallback } from 'react';
import { useNotification } from '../../hooks/useNotification';
import { useErrorInputs } from '../../hooks/useErrorInputs';
import { useApiRequest } from '../../hooks/useApiRequest';
import { useDataAuth } from '../../hooks/useDataAuth';
import { useTranslation } from "react-i18next";
import FormControlLabel from '@mui/material/FormControlLabel';
import TableContainer from '@mui/material/TableContainer';
import PostAddIcon from '@mui/icons-material/PostAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import TableBody from '@mui/material/TableBody';
import CheckIcon from '@mui/icons-material/Check';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TextField from '@mui/material/TextField';
import EditIcon from '@mui/icons-material/Edit';
import TableRow from '@mui/material/TableRow';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';

import { fetchServiceJson } from '../../services/common.services';
import { SearchProduct } from './dialogs/SearchProduct';
import { formatNumber } from '../../utils/invoice';

import '../../styles/components/_forms.scss';
export const ProductsForm = forwardRef(({
    dto,
    refreshTotals,
    handleInputChange,
    handleDirectInputChange,
    isOpportunity = true,
    edit = false,
    isView
}, ref) => {
    const products = dto.products;

    const [sendNotification] = useNotification();
    const [{ userSession }] = useDataAuth();
    const apiRequest = useApiRequest();
    const { t } = useTranslation();

    const GetInitialStateProduct = () => ({
        id: new Date().getTime().toString(),
        id_product_service:'',
        code: '',
        name: '',
        quantity: '',
        unitMeasurementId: '',
        price: '',
        discount: 0,
        tax: '',
        total: 0,
        isCreated: true,
        isDisabled: false
    });
    
    const [productos, setProductos] = useState([GetInitialStateProduct()]);
    const [errorInputs, handleErrorInput, resetErrors] = useErrorInputs({});
    const [openDialog, setOpenDialog] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [secuence, setSecuence] = useState(true);
    const [taxes, setTaxes] = useState([]);

    useEffect(() => {
        if (products.length > 0) {
            setProductos(products);
            calcularTotalesGenerales();
        }
    },[products, refreshTotals])
   
    const requiredField = ['code', 'name', 'quantity', 'price', 'tax'];

    const validateField = (index, name, value) => {
        if (['isCreated'].includes(name)) {
            handleErrorInput(`${index}-${name}`, { isError: true, messageError: 'isCreated' });
            return true;
        }

        if (typeof value !== 'string') {
            value = String(value || '');
        }

        if (value.trim() === '') {
            handleErrorInput(`${index}-${name}`, { isError: true, messageError: `${name} es obligatorio.` });
            return false;
        } else {
            handleErrorInput(`${index}-${name}`, { isError: false, messageError: '' });
            return true;
        }
    };

    const validForm = () => {
        const creating = productos.some(product => product.isCreated);
        if (productos.length > 0 && (productos[0].name !== '' && productos[0].code !== '') && !creating) {
            return true;
        }
        return false;
    };

    useImperativeHandle(ref, () => ({
        getData: () => productos,
        validForm,
        errorInputs,
    }));

    const getPercent = (searchNum='') => {
        const parts = searchNum.split("("); 
        if (parts.length > 1) {
            const num = parts[1].replace("%)", "").trim(); 
            return parseFloat(num) || 0;
        }
        return 0;
    };

    const calcularTotalesGenerales = () => {
        return productos.reduce(
            (totales, product) => {
                const quantity = parseFloat(product.quantity) || 0;
                const price = parseFloat(product.price) || 0;
                const discountPercent = parseFloat(product.discount) || 0;
                const porcentajeImp = getPercent(product.tax);
                const subtotal = quantity * price;
                const discount = (discountPercent / 100) * subtotal; 
                const totalCondiscount = subtotal - discount;
                const totalImp = totalCondiscount * (porcentajeImp / 100);
                const total = totalCondiscount + totalImp;
                product.total = total;
                totales.price += price;
                totales.tax += totalImp;
                totales.gross_total_value += subtotal;
                totales.quantity += quantity;
                totales.discount += discount;
                totales.totalGeneral += total;
                if (product.monthly_pay) {
                    totales.monthly_price += total;
                }else{
                    totales.singlePayment += total;
                }
                handleDirectInputChange('discount', totales.discount.toString());
                handleDirectInputChange('tax_amount', totales.tax.toString());
                handleDirectInputChange('gross_total_value', totales.gross_total_value.toString());
                handleDirectInputChange('payable_amount', totales.totalGeneral.toString());
                handleDirectInputChange('price', totales.totalGeneral.toString());
                handleDirectInputChange('monthly_price', totales.monthly_price.toString());
                return totales;
            },
            {
                tax: 0,
                price: 0,
                gross_total_value:0,
                quantity: 0,
                discount: 0,
                totalGeneral: 0,
                singlePayment: 0,
                monthly_price: 0,
            }
        );
    };

    const handleFieldChange = (index, field, value) => {
        if (field === 'discount') {
            value = value === '' ? 0 : value.toString().replace(/^0+(?=\d)/, '');
        }
        const updatedProductos = productos.map((producto, i) => {
            if (i === index) {
                const updatedProduct = { ...producto, [field]: value };
                return updatedProduct;
            }
            return producto;
        });
        setProductos(updatedProductos);
    };

    const handleAddProduct = () => {
        const lastProductIndex = productos.length - 1;
        const lastProduct = productos[lastProductIndex];
        const isValid = requiredField.every((field) => validateField(lastProductIndex, field, lastProduct[field]));
        if (modifyingProduct()) return
        if (!isValid) {
            sendNotification({ msg: t('messages.asteriskRequiredNote'), variant: 'error'});
            return
        }
        setProductos([...productos, { ...GetInitialStateProduct() }]);
    };

    const handleSearch = useCallback((value, action=null) => {
        setTimeout(async () => {
            let variables = { "code_sku": value };
            if (action === 'SELECT_PRODUCT_SERVICE_LIKE'){
                variables = {"name": value}
            }
            const data = await apiRequest({
                url: '',
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: JSON.stringify({
                    "process": "product_service_select",
                    "action": action ? action :"SELECT_BY_SERVICE_CODE",
                    "variables": JSON.stringify({ ...variables, id_company: dto.id_company })
                }) 
            });
            if (data?.length > 0) {
                if (action){
                    setSearchResults(data.slice(0, 10))
                }else{
                    updateProducts(value, data)
                }
            }else{
                const index = productos.findIndex(product => product.isCreated);
                if(index !== -1){
                    setProductos(productos.map((p, i) => i === index ? {...GetInitialStateProduct()} : p));
                    sendNotification({ msg: t('validation.codeExistingProduct'), variant: 'info' });
                }
            }
        }, 3000);
    }, [apiRequest]); 

    const updateProducts = (value, data) => {
        setProductos(prevProductos => {
            let currentIndex = prevProductos.findIndex(i => i.isCreated || i.code === value);
            let updatedProducts = [...prevProductos];
            if (currentIndex !== -1) {
                updatedProducts[currentIndex] = { 
                    ...updatedProducts[currentIndex], 
                    ...data[0], 
                    id: new Date().getTime().toString(),
                    code: data[0]?.code_sku,
                    isCreated: true,
                    isDisabled: true,
                    unit_code: data[0]?.unit_code.toString() || '',
                    tax: isSpecialProduct(data[0]?.code_sku) ? 'IVA - (19%)' : data[0]?.tax || '',
                };
            } else {
                updatedProducts.push({ 
                    ...data[0], 
                    id: new Date().getTime().toString(),
                    code: value,
                    unit_code: data[0]?.unit_code.toString() || '', 
                    isCreated: true,
                    isDisabled: true
                });
            }
            return updatedProducts;
        });
    };   

    const [totals, setTotals] = useState({
        discount: 0,
        tax: 0,
        totalGeneral: 0,
    })

    const handleSaveProduct = (index) => {
        let product = productos[index];
        const isValid = requiredField.every((field) => validateField(index, field, product[field]));
        if (isValid) {
            const quantity = parseFloat(product.quantity) || 0;
            const price = parseFloat(product.price) || 0;
            const discount = parseFloat(product.discount) || 0;
            const porcentajeImp = getPercent(product.tax);
            const subtotal = quantity * price;
            const totalCondiscount = subtotal - discount;
            const totalImp = totalCondiscount * (porcentajeImp / 100);
            const total = totalCondiscount + totalImp;
            product.total = total;
            updateProductsCreated(productos, index);
            sendNotification({ msg: t('messages.saveSuccess'), variant: 'success' });
            const totales = calcularTotalesGenerales();
            setTotals(totales);
            resetErrors();
        } else {
            sendNotification({ msg: t('validation.requiredFieldsMissing'), variant: 'error' });
        }
    };

    const updateProductsCreated = (productosList, index, del = false) => {
        const updated = productosList.map((prod, i) => {
            if (del) {
                if (i === index) {
                    return { ...prod, isCreated: true };
                }
            } else {
                if (i === index || !secuence) {
                    return { ...prod, isCreated: false };
                } else if (i === index + 1) {
                    return { ...prod, isCreated: true };
                }
            }
            return prod;
        });
    
        setProductos(updated);
        handleDirectInputChange('products', updated);
    };
    
    const handleEditProduct = (index) => {
        let creatingProduct = productos.some(product => product.isCreated);
        if (creatingProduct) return 
        const updatedProductos = productos.map((prod, i) =>
            i === index ? { ...prod, isCreated: true, isDisabled:true } : prod
        );
        setSecuence(false)
        setProductos(updatedProductos);
    };

    const handleDeleteProduct = (index) => {
        let updatedProductos = productos.filter((_, i) => i !== index);
        if (updatedProductos.length === 0) {
            updatedProductos.push({ ...GetInitialStateProduct() });
        }
        updateProductsCreated(updatedProductos, index, true);
        setTimeout(() => {
            const totales = calcularTotalesGenerales();
            setTotals(totales);
        }, 800);
    };
    

    const required = () => {
        return <Typography variant="subtitle2" component="span" color="error" sx={{ mr: 0.5 }}>
            *
        </Typography>
    }

    const hasErrors = () => Object.values(errorInputs).some(field => field.isError);
    
    const modifyingProduct = () => {
        const modifying = productos.some(product => product.isCreated  && (product.code !== '' || product.name !== ''));
        if (modifying && productos.length > 0) {
            sendNotification({ msg: t('validation.creatingProduct'), variant: 'error'});
        }
        return modifying;
    }

    const isSpecialProduct = (code) =>  ['001', '002', '003', '004', '005'].includes(code);
    
    const openDialogSearchProduct = () => {
        if(modifyingProduct()) return
        resetErrors();
        setOpenDialog(!openDialog);
    };

    
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetchServiceJson("MENU-ORDEN_DE_COMPRA", userSession.token);
                if (!response?.data) return;
    
                const parsedData = response.data.reduce((acc, item) => {
                    if (item.field === "tax") {
                        acc.tax = JSON.parse(item.value); 
                    }
                    return acc;
                }, {});
    
                if (parsedData.tax) setTaxes(parsedData.tax);
    
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };
    
        fetchData();
    }, [userSession.token]);
    
    
    useEffect(() => {
        if (Array.isArray(products) && products.length > 0) {
            setTotals(calcularTotalesGenerales());
        }
    }, [products, productos]);
    
    const dialogsProps = {
        openDialog, 
        openDialogSearchProduct, 
        updateProducts,
        searchResults,
        handleSearch
    }
    return (
        <>
            <Tooltip title={t('add')} >
                <IconButton
                    aria-label="add"
                    onClick={handleAddProduct}
                    sx={{color: '#6728AF'}}
                    disabled={isView}
                >
                    <PostAddIcon />
                </IconButton>
            </Tooltip>
            <Tooltip title={t('search')}>
                <IconButton
                    aria-label="add"
                    onClick={openDialogSearchProduct}
                    sx={{color: '#6728AF'}}
                    disabled={isView}
                >
                    <SearchIcon />
                </IconButton>
            </Tooltip>
            <TableContainer
                component={Paper}
                sx={{
                    mb: 3,
                    bgcolor: '#f0f1f5',
                    overflowX: 'auto',
                }}
            >
                <Box>
                    <Typography variant="inherit" align="left">
                        {t("messages.asteriskRequiredNote")}
                    </Typography>
                </Box>
                <Table sx={{ minWidth: 1000 }} aria-label="productos table">
                    <TableHead>
                        <TableRow key={'rowHead'} sx={{ bgcolor: '#e0e0e0' }}>
                            <TableCell key={'codeLabel'}>
                                <Typography variant="subtitle2" component="span">
                                    {required()}{t('crm.products.code')}
                                </Typography>
                            </TableCell>
                            <TableCell key={'descriptionLabel'}>
                                <Typography variant="subtitle2" component="span">
                                    {required()}{t('crm.products.name')}
                                </Typography>
                            </TableCell>
                            <TableCell key={'cantidadLabel'}>
                                <Typography variant="subtitle2" component="span">
                                    {required()}{t('crm.products.quantity')}
                                </Typography>
                            </TableCell>
                            <TableCell key={'valueUnitLabel'}>
                                <Typography variant="subtitle2" component="span">
                                    {required()}{t('crm.products.unitValue')}
                                </Typography>
                            </TableCell>
                            <TableCell key={'discountLabel'}>
                                <Typography variant="subtitle2" component="span">
                                    {`${t('crm.products.discount')} (%)`}
                                </Typography>
                            </TableCell>
                            <TableCell key={'taxLabel'}>
                                <Typography variant="subtitle2" component="span">
                                    {required()}{t('crm.products.taxes')}
                                </Typography>
                            </TableCell>
                            <TableCell key={'taxLabel'}>
                                <Typography variant="subtitle2" component="span">
                                    {t('crm.products.monthlyPayment')}
                                </Typography>
                            </TableCell>
                            <TableCell key={'totalLabel'} padding='normal'>
                                {t('crm.products.total')}
                            </TableCell>
                            <TableCell
                                key={'actionLabel'}
                                padding='normal'
                                sx={{ minWidth: 80 }}
                            >
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            productos.map((product, index) => (
                                <TableRow key={index} sx={{ backgroundColor: product.isCreated && 'white' }}>
                                    <TableCell key={'code'}>
                                        <TextField fullWidth required size="small"
                                            value={product.code}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                handleFieldChange(index, 'code', value);
                                            }}
                                            onBlur={(e) => {
                                                const value = e.target.value;
                                                if (value.length >= 3) {
                                                    const isDuplicate = productos.some((producto, i) => 
                                                        i !== index && producto?.code === value
                                                    );
                                                    if (isDuplicate) {
                                                        sendNotification({ msg: t('validation.productsExisting'), variant: 'info' });
                                                        return;
                                                    }
                                                    handleSearch(value);
                                                }
                                                if (isSpecialProduct(value)) {
                                                    handleFieldChange(index, 'isDisabled', true);
                                                }
                                            }}
                                            sx={{ width: '115%' }}
                                            error={!!errorInputs[`${index}-code`]?.isError}
                                            disabled={!product.isCreated || product.isDisabled}
                                        />

                                    </TableCell>
                                    <TableCell key={'name'}>
                                        <TextField fullWidth required disabled
                                            value={product.name}
                                            onChange={(e) => handleFieldChange(index, 'name', e.target.value)}
                                            size="small"
                                            sx={{ width: '115%' }}
                                            error={!!errorInputs[`${index}-name`]?.isError}
                                        />
                                    </TableCell>
                                    <TableCell key={'quantity'}>
                                        <TextField fullWidth required size="small" type='number' className='input-no-spinner'
                                            value={product.quantity}
                                            onChange={(e) => handleFieldChange(index, 'quantity', e.target.value)}
                                            sx={{ width: '115%' }}
                                            error={!!errorInputs[`${index}-quantity`]?.isError}
                                            disabled={!product.isCreated}
                                        />
                                    </TableCell>
                                    <TableCell key={'price'}>
                                        <TextField fullWidth required  size="small" type='number'className='input-no-spinner'
                                            value={product.price}
                                            onChange={(e) => handleFieldChange(index, 'price', e.target.value)}
                                            sx={{ width: '115%' }}
                                            error={!!errorInputs[`${index}-price`]?.isError}
                                            disabled={!product.isCreated}
                                        />
                                    </TableCell>
                                    <TableCell key={'discount'}>
                                        <TextField fullWidth required  size="small" type='number' className='input-no-spinner'
                                            value={product.discount}
                                            onChange={(e) => handleFieldChange(index, 'discount', e.target.value)}
                                            sx={{ width: '115%' }}
                                            error={!!errorInputs[`${index}-discount`]?.isError}
                                            disabled={!product.isCreated}
                                        />
                                    </TableCell>
                                    <TableCell key={'tax'}>
                                        <FormControl fullWidth required size="small"
                                            error={!!errorInputs.tax?.isError}
                                            // disabled={!product.isCreated || isSpecialProduct(product.code)}
                                            disabled={!product.isCreated}
                                        >
                                            <Select
                                                value={product.tax}
                                                onChange={(e) => handleFieldChange(index, 'tax', e.target.value)}
                                                size="small"
                                                sx={{ width: 120 }}
                                                MenuProps={{ PaperProps: { style: { maxHeight: 200, overflowY: "auto" }} }}
                                            >
                                                {taxes.map((tax, i) => <MenuItem key={i} value={tax.code}> {tax.name}%</MenuItem> )}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell key={'monthly_pay'}>
                                        <FormControl size="small">
                                            <FormControlLabel
                                                control={
                                                    <Checkbox 
                                                    // disabled={!product.isCreated} 
                                                        name=""
                                                        checked={product.monthly_pay === 1} 
                                                        onChange={(e) => handleFieldChange(index, 'monthly_pay', e.target.checked ? 1 : 0)}
                                                    />
                                                }
                                            />
                                        </FormControl>
                                    </TableCell>
                                    <TableCell>
                                        {formatNumber(product.total)}
                                    </TableCell>
                                    <TableCell>
                                        <IconButton disabled={isView}
                                            title={product.isCreated ? 'Guardar' : 'Editar'}
                                            color={product.isCreated ? 'success' : 'primary'}
                                            onClick={() => (product.isCreated ? handleSaveProduct(index) : handleEditProduct(index))}
                                        >
                                            { product.isCreated ? <CheckIcon /> : <EditIcon  /> }
                                        </IconButton>
                                        <IconButton disabled={isView}
                                            color="secondary" 
                                            onClick={() => handleDeleteProduct(index)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        }
                    </TableBody>
                </Table>
            </TableContainer >

            <Box sx={{ mb: 3 }}>
                <Grid container spacing={9} alignItems="flex-start">
                    {!isOpportunity ?
                        <Grid size={{ xs: 12, md:6 }}>
                            <Typography variant="h6" gutterBottom>{t('crm.notesOrderReference')}</Typography>
                            <TextField
                                label={t('crm.observations')}
                                name="observations"
                                size="small"
                                multiline
                                rows={4}
                                value={dto.observations}
                                onChange={handleInputChange}
                            />
                        </Grid>
                    :<Grid size={{ xs: 12, sm: 6 }}></Grid>}
                    <Grid size={{ xs: 12, md:6 }} sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                        <Typography variant="subtitle1">Totales</Typography>
                        <Box
                            sx={{
                                textAlign: "right",
                                p: 2,
                                borderRadius: 2,
                                bgcolor: "grey.100",
                                boxShadow: 3,
                                minWidth: "60%",
                                marginTop:'10px' 
                            }}
                        >
                            <Typography variant="body2" fontWeight="bold">{t('crm.products.discountTotal')}: {formatNumber(totals.discount)}</Typography>
                            <Typography variant="body2" fontWeight="bold">{t('crm.products.taxTotal')}: {formatNumber(totals.tax)}</Typography>
                            {dto.total_retentions !== undefined && (
                                <Typography variant="body2" fontWeight="bold">{t('commonFields.retentions')}: {formatNumber(dto.total_retentions)}</Typography>
                            )}
                            <Typography variant="body2" fontWeight="bold">{t('crm.products.monthlyPriceTotal')}: {formatNumber(totals.monthly_price)}</Typography>
                            <Typography variant="body2" fontWeight="bold">{t('crm.products.paymentOneTimeTotal')}: {formatNumber(totals.singlePayment)}</Typography>
                            <Typography variant="body2" fontWeight="bold">{t('commonFields.grossTotalValue')}: {formatNumber(totals.gross_total_value)}</Typography>
                            <Typography variant="body2" fontWeight="bold">{t('crm.products.generalTotal')}: {formatNumber(totals.totalGeneral)}</Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            <SearchProduct {...dialogsProps} />
        </>
    );
});